if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.easyPointsObserver = null;
        this.easyPointsRaf = 0;
        this.easyPointsVariantUnsubscriber = null;
        this.easyPointsConfig = {
          show: true,
          title: 'Gana [points] EASYPOINTS',
          subtext: 'por comprar este producto',
        };
        this.handleEasyPointsVariantChange = this.handleEasyPointsVariantChange.bind(this);

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
          if (this.hasAttribute('open')) {
            requestAnimationFrame(() => this.startEasyPoints(target));
          }
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.stopEasyPoints();
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then(async (responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            const pointsConfig = responseHTML.querySelector('[data-eg-purchase-modern-config-v2]');

            if (!productElement) throw new Error('Quick Add product-info was not found in the product response.');

            this.easyPointsConfig = {
              show: pointsConfig ? pointsConfig.dataset.showPoints === 'true' : true,
              title: pointsConfig?.dataset.pointsTitle || 'Gana [points] EASYPOINTS',
              subtext: pointsConfig?.dataset.pointsSubtext || 'por comprar este producto',
            };

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            this.installPdpConfigStyle(responseHTML);

            const renderedProduct = this.modalContent.querySelector('product-info');
            this.setEasyPointsLoading(renderedProduct);
            await this.ensurePurchaseStylesheet(responseHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.modalContent.querySelector('product-component')?.dispatchViewEvent?.();

            super.show(opener);
            this.startEasyPoints(renderedProduct);
          })
          .catch((error) => {
            console.error('[EASY GAMES] Quick Add failed to load product.', error);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient') {
            this.modalContent.classList.add(classApplied);
          }
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        productElement.querySelectorAll('modal-dialog').forEach((modal) => modal.remove());

        // EASYPOINTS ownership: keep Froonze's official Product Points app block mounted.
        // EASY GAMES only mirrors its published value into the branded shell.
      }

      pointsSourceSelector() {
        return '.frcp-points-preview--visible, .frcp-points-preview';
      }

      isEasyPointsProvider(provider) {
        if (!provider) return false;
        const providerId = String(provider.id || '');
        return providerId.endsWith('__points') || Boolean(provider.querySelector(this.pointsSourceSelector()));
      }

      findEasyPointsProvider(root) {
        if (!root?.querySelectorAll) return null;
        return Array.from(root.querySelectorAll('.shopify-block, .shopify-app-block')).find((provider) =>
          this.isEasyPointsProvider(provider)
        ) || null;
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;
        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;

        // Froonze's Product Points block is an app-owned runtime boundary. Dawn IDs
        // are namespaced for Quick Add, but the app block itself must keep the exact
        // server-rendered identity and internal references that Froonze generated.
        const pointsProvider = this.findEasyPointsProvider(productElement);
        const pointsPlaceholder = pointsProvider ? document.createElement('template') : null;
        if (pointsPlaceholder) {
          pointsPlaceholder.dataset.egPointsProviderPlaceholder = 'true';
          pointsProvider.replaceWith(pointsPlaceholder);
        }

        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        if (pointsProvider) {
          productElement
            .querySelector('template[data-eg-points-provider-placeholder]')
            ?.replaceWith(pointsProvider);
        }

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      installPdpConfigStyle(responseHTML) {
        this.modalContent.querySelector('[data-eg-quick-add-pdp-config]')?.remove();
        const sourceStyle = responseHTML.querySelector('.eg-pdp-system-v2 style');
        if (!sourceStyle?.textContent) return;

        const style = document.createElement('style');
        style.dataset.egQuickAddPdpConfig = 'true';
        style.textContent = sourceStyle.textContent;
        this.modalContent.prepend(style);
      }

      ensurePurchaseStylesheet(responseHTML) {
        const selector = 'link[href*="easygames-product-purchase-modern.css"]';
        if (document.querySelector(selector)) return Promise.resolve();

        const sourceLink = responseHTML.querySelector(selector);
        if (!sourceLink?.href) {
          console.warn('[EASY GAMES] Purchase Modern stylesheet was not found in product response.');
          return Promise.resolve();
        }

        return new Promise((resolve) => {
          const link = document.createElement('link');
          let settled = false;
          const finish = () => {
            if (settled) return;
            settled = true;
            resolve();
          };

          link.rel = 'stylesheet';
          link.href = sourceLink.href;
          link.dataset.egQuickAddPurchaseStylesheet = 'true';
          link.addEventListener('load', finish, { once: true });
          link.addEventListener('error', finish, { once: true });
          document.head.appendChild(link);
          window.setTimeout(finish, 2500);
        });
      }

      pointsValue(text) {
        return ((String(text || '').replace(/\u00a0/g, ' ').match(/[0-9][0-9.,]*/) || [])[0] || '');
      }

      pointsShell(productElement) {
        return productElement?.querySelector('[data-eg-points-shell]');
      }

      setEasyPointsLoading(productElement) {
        const shell = this.pointsShell(productElement);
        if (!shell) return;

        productElement.dataset.egPointsState = this.easyPointsConfig.show ? 'loading' : 'disabled';
        if (this.easyPointsConfig.show) productElement.style.removeProperty('--egpm-qty-span');
        else productElement.style.setProperty('--egpm-qty-span', '12');

        shell.hidden = !this.easyPointsConfig.show;
        const title = shell.querySelector('[data-eg-points-title]');
        const subtext = shell.querySelector('[data-eg-points-subtext]');
        const copy = shell.querySelector('.egp-points-shell__copy');
        const icon = shell.querySelector('.egp-points-shell__icon');

        if (!this.easyPointsConfig.show) {
          if (copy) copy.style.removeProperty('visibility');
          if (icon) icon.style.removeProperty('opacity');
          shell.removeAttribute('aria-busy');
          shell.removeAttribute('aria-hidden');
          shell.removeAttribute('aria-label');
          return;
        }

        if (copy) copy.style.removeProperty('visibility');
        if (icon) icon.style.opacity = '0.55';
        if (title) title.textContent = 'Calculando EASYPOINTS…';
        if (subtext) {
          subtext.textContent = '';
          subtext.hidden = true;
        }
        shell.dataset.egPointsState = 'loading';
        shell.setAttribute('aria-busy', 'true');
        shell.removeAttribute('aria-hidden');
        shell.setAttribute('aria-label', 'Calculando EASYPOINTS');
      }

      setEasyPointsValue(productElement, points) {
        const shell = this.pointsShell(productElement);
        if (!shell || !points || !this.easyPointsConfig.show) return;

        const title = shell.querySelector('[data-eg-points-title]');
        const subtext = shell.querySelector('[data-eg-points-subtext]');
        const copy = shell.querySelector('.egp-points-shell__copy');
        const icon = shell.querySelector('.egp-points-shell__icon');
        const titleText = this.easyPointsConfig.title.split('[points]').join(points);

        productElement.dataset.egPointsState = 'ready';
        productElement.style.removeProperty('--egpm-qty-span');
        shell.hidden = false;
        if (copy) copy.style.removeProperty('visibility');
        if (icon) icon.style.removeProperty('opacity');
        if (title && title.textContent !== titleText) title.textContent = titleText;
        if (subtext) {
          if (subtext.textContent !== this.easyPointsConfig.subtext) subtext.textContent = this.easyPointsConfig.subtext;
          subtext.hidden = !this.easyPointsConfig.subtext;
        }
        shell.dataset.egPointsState = 'ready';
        shell.setAttribute('aria-busy', 'false');
        shell.removeAttribute('aria-hidden');
        shell.setAttribute(
          'aria-label',
          `${titleText}${this.easyPointsConfig.subtext ? '. ' + this.easyPointsConfig.subtext : ''}`
        );
      }

      setEasyPointsUnavailable(productElement) {
        const shell = this.pointsShell(productElement);
        if (!shell) return;

        productElement.dataset.egPointsState = 'unavailable';
        productElement.style.setProperty('--egpm-qty-span', '12');
        shell.dataset.egPointsState = 'unavailable';
        shell.removeAttribute('aria-busy');
        shell.removeAttribute('aria-hidden');
        shell.removeAttribute('aria-label');
        shell.hidden = true;
      }

      markEasyPointsProvider(source) {
        const provider = source?.closest('.shopify-block, .shopify-app-block');
        if (!provider) return null;

        provider.classList.add('egp-points-provider');
        provider.setAttribute('aria-hidden', 'true');
        return provider;
      }

      syncEasyPointsFromProvider(productElement) {
        if (!productElement?.isConnected || !this.easyPointsConfig.show) return false;

        const source = productElement.querySelector(this.pointsSourceSelector());
        if (!source) return false;

        this.markEasyPointsProvider(source);
        const points = this.pointsValue(source.textContent);
        if (!points) return false;

        this.setEasyPointsValue(productElement, points);
        return true;
      }

      scheduleEasyPointsSync(productElement) {
        if (this.easyPointsRaf) return;
        this.easyPointsRaf = requestAnimationFrame(() => {
          this.easyPointsRaf = 0;
          this.syncEasyPointsFromProvider(productElement);
        });
      }

      observeEasyPoints(productElement) {
        this.easyPointsObserver?.disconnect();
        this.easyPointsObserver = null;

        if (!productElement?.isConnected || !this.easyPointsConfig.show) return;

        this.easyPointsObserver = new MutationObserver(() => this.scheduleEasyPointsSync(productElement));
        this.easyPointsObserver.observe(productElement, {
          childList: true,
          subtree: true,
          characterData: true,
        });

        this.scheduleEasyPointsSync(productElement);
      }

      replaceEasyPointsProvider(productElement, sourceDocument) {
        if (!productElement?.isConnected || !sourceDocument) return false;

        const currentProvider = this.findEasyPointsProvider(productElement);
        const freshProvider = this.findEasyPointsProvider(sourceDocument);

        if (!freshProvider) {
          console.warn('[EASY GAMES] Froonze Product Points block was not found in the variant response.');
          this.setEasyPointsUnavailable(productElement);
          return false;
        }

        if (!currentProvider?.parentNode) {
          console.warn('[EASY GAMES] Froonze Product Points block was not found in the active Quick Add modal.');
          this.setEasyPointsUnavailable(productElement);
          return false;
        }

        const marker = document.createComment('eg-froonze-points-provider');
        currentProvider.replaceWith(marker);

        const wrapper = document.createElement('div');
        HTMLUpdateUtility.setInnerHTML(wrapper, freshProvider.outerHTML);
        const nextProvider = wrapper.firstElementChild;

        if (!nextProvider) {
          marker.replaceWith(currentProvider);
          console.warn('[EASY GAMES] Froonze Product Points block could not be rehydrated.');
          return false;
        }

        marker.replaceWith(nextProvider);
        this.scheduleEasyPointsSync(productElement);
        return true;
      }

      startEasyPoints(productElement) {
        if (!productElement?.isConnected) return;

        this.stopEasyPoints();
        this.setEasyPointsLoading(productElement);
        if (!this.easyPointsConfig.show) return;

        if (!this.findEasyPointsProvider(productElement)) {
          console.warn('[EASY GAMES] Froonze Product Points app block is missing from Quick Add.');
          this.setEasyPointsUnavailable(productElement);
          return;
        }

        this.observeEasyPoints(productElement);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this.easyPointsVariantUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.handleEasyPointsVariantChange
          );
        }
      }

      handleEasyPointsVariantChange(event) {
        const productElement = this.modalContent.querySelector('product-info');
        const data = event?.data;
        if (!productElement?.isConnected || !data) return;

        const eventSectionId = String(data.sectionId || '');
        const currentSectionId = String(productElement.dataset.section || '');
        const originalSectionId = String(productElement.dataset.originalSection || '');
        if (eventSectionId !== currentSectionId && eventSectionId !== originalSectionId) return;

        this.setEasyPointsLoading(productElement);
        this.replaceEasyPointsProvider(productElement, data.html);
      }

      stopEasyPoints() {
        this.easyPointsObserver?.disconnect();
        this.easyPointsObserver = null;
        if (this.easyPointsRaf) cancelAnimationFrame(this.easyPointsRaf);
        this.easyPointsRaf = 0;
        this.easyPointsVariantUnsubscriber?.();
        this.easyPointsVariantUnsubscriber = null;
      }
    }
  );
}
